// Context-aware Node Detail Modal. Renders different content for structural
// containers vs deployable resources, with clickable "Contains" / "Connected to"
// chips that re-focus the canvas on the referenced node.

import { ExternalLink, Link2, MapPin, Layers, Network, BookOpen, Coins, X, Pencil, Trash2, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  getStructuralMeta,
  PROVIDER_LABELS,
  PROVIDER_ACCENTS,
} from "../../lib/archModel";

function Section({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <div
        className="font-mono"
        style={{
          fontSize: 10,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--color-text-tertiary)",
          marginBottom: 8,
          fontWeight: 600,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Chip({ label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="motion-micro"
      style={{
        background: "var(--color-background-tertiary)",
        border: "1px solid var(--color-border-primary)",
        color: "var(--color-text-secondary)",
        fontSize: 12,
        padding: "5px 10px",
        borderRadius: 9999,
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--color-accent)";
        e.currentTarget.style.color = "var(--color-text-primary)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--color-border-primary)";
        e.currentTarget.style.color = "var(--color-text-secondary)";
      }}
    >
      {label}
    </button>
  );
}

export default function NodeDetailModal({
  open,
  onClose,
  node,
  parentNode,
  childNodes = [],
  peerConnections = [],
  allNodes = [],
  provider,
  onFocusNode,
  onStartAddChild,
  onStartAddPeer,
  onRequestRename,
  onRequestRemove,
  onRemovePeer,
}) {
  if (!node) return null;
  const isStructural = node.kind === "structural";
  const meta = isStructural ? getStructuralMeta(node.type) : null;
  const providerLabel = PROVIDER_LABELS[provider] || provider;
  const providerColor = PROVIDER_ACCENTS[provider] || "#64748B";

  const peerTouching = peerConnections.filter(
    (p) => p.sourceId === node.id || p.targetId === node.id
  );

  const handleNavigate = (id) => {
    onFocusNode?.(id);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="node-detail-modal"
        className="max-w-[640px] max-h-[88vh] overflow-y-auto"
        style={{
          background: "var(--color-background-primary)",
          border: "1px solid var(--color-border-primary)",
          color: "var(--color-text-primary)",
        }}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            {isStructural ? (
              <span
                className="font-mono"
                style={{
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  padding: "3px 8px",
                  borderRadius: 9999,
                  background: `color-mix(in oklab, ${meta.color} 14%, transparent)`,
                  border: `1px solid color-mix(in oklab, ${meta.color} 45%, transparent)`,
                  color: meta.color,
                  fontWeight: 600,
                }}
              >
                {meta.label}
              </span>
            ) : (
              <span
                className="font-mono"
                style={{
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  padding: "3px 8px",
                  borderRadius: 9999,
                  background: "var(--color-background-tertiary)",
                  border: "1px solid var(--color-border-primary)",
                  color: "var(--color-text-secondary)",
                  fontWeight: 600,
                }}
              >
                Resource · {node.config?.tier || "compute"}
              </span>
            )}
            <span
              className="font-mono"
              style={{
                fontSize: 10,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "3px 8px",
                borderRadius: 9999,
                background: `color-mix(in oklab, ${providerColor} 14%, transparent)`,
                border: `1px solid color-mix(in oklab, ${providerColor} 45%, transparent)`,
                color: providerColor,
                fontWeight: 600,
              }}
              data-testid="modal-provider-badge"
            >
              {providerLabel}
            </span>
          </div>
          <DialogTitle
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "-0.015em",
              color: "var(--color-text-primary)",
              marginTop: 8,
            }}
            data-testid="modal-node-name"
          >
            {node.name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Architecture details for {node.name}
          </DialogDescription>
          <div
            className="font-mono"
            style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}
            data-testid="modal-node-type"
          >
            {node.type}
          </div>
        </DialogHeader>

        {(onStartAddChild || onStartAddPeer || onRequestRename || onRequestRemove) && (
          <div
            data-testid="modal-actions-toolbar"
            className="flex items-center flex-wrap gap-2"
            style={{
              borderBottom: "1px solid var(--color-border-primary)",
              paddingBottom: 12,
              marginBottom: 4,
            }}
          >
            {onStartAddChild && (
              <ActionButton testid="modal-action-add-child" icon={<Plus size={13} />} label="Add child" onClick={() => onStartAddChild(node)} />
            )}
            {onStartAddPeer && (
              <ActionButton testid="modal-action-add-peer" icon={<Link2 size={13} />} label="Add peer connection" onClick={() => onStartAddPeer(node)} />
            )}
            {onRequestRename && (
              <ActionButton testid="modal-action-rename" icon={<Pencil size={13} />} label="Rename" onClick={() => onRequestRename(node)} />
            )}
            {onRequestRemove && node.parentId && (
              <ActionButton
                testid="modal-action-remove"
                icon={<Trash2 size={13} />}
                label="Remove"
                onClick={() => onRequestRemove(node)}
                danger
              />
            )}
          </div>
        )}

        <div className="flex flex-col gap-6 mt-2">
          {node.description && (
            <Section title={isStructural ? "Description" : "What it does"} testid="modal-section-description">
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: "var(--color-text-primary)" }}>
                {node.description}
              </p>
            </Section>
          )}

          {/* Shown for EVERY node, structural containers included — an unexplained
              box in someone's architecture is exactly what this section exists to
              answer. Nodes the user added by hand have no generated rationale, so
              they get an honest note instead of a silently missing section. */}
          <Section
            title={
              node.reasoning
                ? "Why it was recommended"
                : "Why it's here"
            }
            testid="modal-section-reasoning"
          >
            <p
              style={{
                fontSize: 13,
                lineHeight: 1.6,
                color: node.reasoning
                  ? "var(--color-text-secondary)"
                  : "var(--color-text-tertiary)",
                fontStyle: node.reasoning ? "normal" : "italic",
                background: "var(--color-background-secondary)",
                border: "1px solid var(--color-border-primary)",
                borderRadius: 10,
                padding: "12px 14px",
              }}
              data-testid="modal-reasoning-text"
            >
              {node.reasoning ||
                "You added this one yourself, so there's no generated rationale for it. Everything the generator proposes comes with an explanation here."}
            </p>
          </Section>

          {parentNode && (
            <Section title="Lives in" testid="modal-section-lives-in">
              <Chip
                label={`${parentNode.name}`}
                onClick={() => handleNavigate(parentNode.id)}
                testid={`modal-parent-chip-${parentNode.id}`}
              />
            </Section>
          )}

          {!isStructural && (
            <Section title="Configuration" testid="modal-section-config">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                <ConfigCell icon={<MapPin size={14} />} label="Planned region" value={node.config?.region || "Not specified"} />
                <ConfigCell icon={<Layers size={14} />} label="Proposed SKU" value={node.config?.sku || "Not specified"} />
                <ConfigCell icon={<Network size={14} />} label="Proposed redundancy" value={node.config?.redundancy || "Not specified"} />
                <ConfigCell
                  icon={<Coins size={14} />}
                  label="Est. monthly cost"
                  value={
                    typeof node.config?.estMonthlyCost === "number"
                      ? `$${node.config.estMonthlyCost}/mo`
                      : "—"
                  }
                />
              </div>
            </Section>
          )}

          {(node.config?.logicalName || node.config?.deployName) && (
            <Section title="Resource names" testid="modal-section-resource-names">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <ConfigCell label="Display name" value={node.name} />
                <ConfigCell label="Stable diagram ID" value={node.id} />
                <ConfigCell label="Production logical name" value={node.config?.logicalName || "Not specified"} />
                <ConfigCell label="Proposed cloud name" value={node.config?.deployName || "Not specified"} />
              </div>
            </Section>
          )}

          {node.config?.networkPlacement?.mode === "managed_public_endpoint" && (
            <Section title="Network placement" testid="modal-section-network-placement">
              <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--color-text-secondary)" }}>
                Managed public endpoint outside your VPC. Its outbound VPC connector
                uses private subnets: {(node.config.networkPlacement.vpc_connector_subnet_ids || []).join(", ") || "not specified"}.
              </p>
            </Section>
          )}

          {isStructural && childNodes.length > 0 && (
            <Section title={`Contains (${childNodes.length})`} testid="modal-section-contains">
              <div className="flex flex-wrap gap-1.5">
                {childNodes.map((c) => (
                  <Chip
                    key={c.id}
                    label={c.name}
                    onClick={() => handleNavigate(c.id)}
                    testid={`modal-child-chip-${c.id}`}
                  />
                ))}
              </div>
            </Section>
          )}

          {peerTouching.length > 0 && (
            <Section title={`Connected to (${peerTouching.length})`} testid="modal-section-connected">
              <div className="flex flex-col gap-2">
                {peerTouching.map((p) => {
                  const otherId = p.sourceId === node.id ? p.targetId : p.sourceId;
                  const other = allNodes.find((n) => n.id === otherId);
                  if (!other) return null;
                  const direction = p.sourceId === node.id ? "→" : "←";
                  return (
                    <div
                      key={p.id}
                      data-testid={`modal-peer-${p.id}`}
                      className="flex items-center gap-2 flex-wrap"
                    >
                      <Link2 size={12} color="var(--color-text-tertiary)" />
                      <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{p.label || "connects to"}</span>
                      <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{direction}</span>
                      <Chip
                        label={other.name}
                        onClick={() => handleNavigate(other.id)}
                        testid={`modal-peer-chip-${other.id}`}
                      />
                      {onRemovePeer && (
                        <button
                          type="button"
                          onClick={() => onRemovePeer(p.id)}
                          data-testid={`modal-peer-remove-${p.id}`}
                          className="motion-micro inline-flex items-center justify-center"
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 6,
                            background: "transparent",
                            border: "1px solid var(--color-border-primary)",
                            color: "var(--color-text-tertiary)",
                            cursor: "pointer",
                          }}
                          title="Remove connection"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "var(--color-danger)";
                            e.currentTarget.style.borderColor = "var(--color-danger)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "var(--color-text-tertiary)";
                            e.currentTarget.style.borderColor = "var(--color-border-primary)";
                          }}
                        >
                          <X size={11} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {node.docsUrl && (
            <Section title="Documentation" testid="modal-section-docs">
              <a
                href={node.docsUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="motion-micro inline-flex items-center gap-1.5"
                style={{
                  fontSize: 13,
                  color: "var(--color-accent)",
                  fontWeight: 500,
                  textDecoration: "none",
                }}
                data-testid="modal-docs-link"
              >
                <BookOpen size={14} /> Provider docs <ExternalLink size={12} />
              </a>
            </Section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfigCell({ icon, label, value }) {
  return (
    <div
      style={{
        background: "var(--color-background-secondary)",
        border: "1px solid var(--color-border-primary)",
        borderRadius: 10,
        padding: "10px 12px",
      }}
    >
      <div className="flex items-center gap-1.5" style={{ marginBottom: 4, color: "var(--color-text-tertiary)" }}>
        {icon}
        <span
          className="font-mono"
          style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" }}
        >
          {label}
        </span>
      </div>
      <div style={{ fontSize: 13, color: "var(--color-text-primary)", fontWeight: 500 }}>{value}</div>
    </div>
  );
}


function ActionButton({ icon, label, onClick, testid, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="motion-micro inline-flex items-center gap-1.5"
      style={{
        background: danger ? "color-mix(in oklab, var(--color-danger) 10%, transparent)" : "var(--color-background-secondary)",
        border: `1px solid ${danger ? "color-mix(in oklab, var(--color-danger) 40%, transparent)" : "var(--color-border-primary)"}`,
        color: danger ? "var(--color-danger)" : "var(--color-text-primary)",
        fontSize: 12,
        fontWeight: 500,
        padding: "6px 12px",
        borderRadius: 8,
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        if (danger) return;
        e.currentTarget.style.borderColor = "var(--color-accent)";
      }}
      onMouseLeave={(e) => {
        if (danger) return;
        e.currentTarget.style.borderColor = "var(--color-border-primary)";
      }}
    >
      {icon} {label}
    </button>
  );
}
